// Default / normalize / section-map helpers for proposal page bodies.
// Kept separate from proposalPageTypes so the types file stays under size limits.

import type { OpportunityFormatKey } from "../../types/opportunity.js";
import {
  getProposalTextDefault,
  type ProposalTextSectionKey,
} from "./proposalTextSections.js";
import {
  normalizeIntentPageLayout,
  type IntentPageLayout,
} from "./proposalIntentLayout.js";
import {
  defaultCoverSandboxBlocks,
  defaultCostSandboxBlocks,
  defaultIntentSandboxBlocks,
  defaultPaymentSandboxBlocks,
  migrateCoverSandboxBlocks,
  migrateCostSandboxBlocks,
  migrateIntentSandboxBlocks,
  migratePaymentSandboxBlocks,
  coverPartsFromSandboxBlocks,
  introFooterFromSandboxBlocks,
  textPartsFromSandboxBlocks,
} from "./proposalSandboxStarters.js";
import type { ProposalSandboxBlock } from "./proposalSandboxBlocks.js";
import type {
  ProposalPageArchetype,
  ProposalPageBody,
  ProposalPageBodyCost,
  ProposalPageBodyCover,
  ProposalPageBodyIntent,
  ProposalPageBodyPayment,
  ProposalPageSectionPart,
} from "./proposalPageTypes.js";

function part(text: string, included = true): ProposalPageSectionPart {
  return { text, included };
}

/** Build default body for an archetype from format-aware text defaults. */
export function defaultBodyForArchetype(
  formatKey: OpportunityFormatKey,
  archetype: ProposalPageArchetype,
): ProposalPageBody {
  switch (archetype) {
    case "cover_letter": {
      const bodyText = getProposalTextDefault(formatKey, "cover_letter_body");
      const blocks = defaultCoverSandboxBlocks(bodyText);
      const parts = coverPartsFromSandboxBlocks(blocks);
      return { body: part(parts.body.text, parts.body.included), blocks };
    }
    case "cost_breakdown": {
      const blocks = defaultCostSandboxBlocks({
        introText: getProposalTextDefault(formatKey, "cost_breakdown_intro"),
        footerText: getProposalTextDefault(formatKey, "cost_breakdown_footer"),
      });
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        intro: part(parts.intro.text, parts.intro.included),
        footer: part(parts.footer.text, parts.footer.included),
        blocks,
      };
    }
    case "payment_schedule": {
      const blocks = defaultPaymentSandboxBlocks({
        introText: getProposalTextDefault(formatKey, "payment_schedule_intro"),
        footerText: getProposalTextDefault(formatKey, "payment_schedule_footer"),
      });
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        intro: part(parts.intro.text, parts.intro.included),
        footer: part(parts.footer.text, parts.footer.included),
        blocks,
      };
    }
    case "intent_to_purchase": {
      const preambleText = getProposalTextDefault(formatKey, "intent_to_purchase_preamble");
      const signatureIntroText = getProposalTextDefault(
        formatKey,
        "intent_to_purchase_signature_block",
      );
      const blocks = defaultIntentSandboxBlocks({ preambleText, signatureIntroText });
      const parts = textPartsFromSandboxBlocks(blocks);
      return {
        preamble: part(parts.preamble.text, parts.preamble.included),
        signature_block: part(parts.signature_block.text, parts.signature_block.included),
        blocks,
      };
    }
  }
}

export function sandboxBlocksFromPageBody(
  archetype: ProposalPageArchetype,
  body: ProposalPageBody,
): ProposalSandboxBlock[] {
  switch (archetype) {
    case "cover_letter": {
      const b = body as ProposalPageBodyCover;
      return migrateCoverSandboxBlocks({
        blocks: b.blocks,
        bodyText: b.body?.text,
        bodyIncluded: b.body?.included,
      });
    }
    case "cost_breakdown": {
      const b = body as ProposalPageBodyCost;
      return migrateCostSandboxBlocks({
        blocks: b.blocks,
        introText: b.intro?.text,
        introIncluded: b.intro?.included,
        footerText: b.footer?.text,
        footerIncluded: b.footer?.included,
      });
    }
    case "payment_schedule": {
      const b = body as ProposalPageBodyPayment;
      return migratePaymentSandboxBlocks({
        blocks: b.blocks,
        introText: b.intro?.text,
        introIncluded: b.intro?.included,
        footerText: b.footer?.text,
        footerIncluded: b.footer?.included,
      });
    }
    case "intent_to_purchase":
      return intentBlocksFromBody(body as ProposalPageBodyIntent);
  }
}

export function intentBlocksFromBody(body: ProposalPageBodyIntent): ProposalSandboxBlock[] {
  return migrateIntentSandboxBlocks({
    blocks: body.blocks,
    preambleText: body.preamble?.text,
    preambleIncluded: body.preamble?.included,
    signatureIntroText: body.signature_block?.text,
    signatureIntroIncluded: body.signature_block?.included,
    show_customer_sample: body.show_customer_sample,
    signature_parties: body.signature_parties,
  });
}

/** @deprecated Prefer intentBlocksFromBody. */
export function intentLayoutFromBody(body: ProposalPageBodyIntent): IntentPageLayout {
  const blocks = intentBlocksFromBody(body);
  return normalizeIntentPageLayout({
    show_customer_sample: blocks.some(
      (b) => b.type === "customer_sample" && b.included !== false,
    ),
    signature_parties: blocks
      .filter((b) => b.type === "signature_party")
      .map((b) => ({
        id: b.id,
        title: b.title,
        fields: b.fields,
      })),
  });
}

export type SectionOverrideLike = {
  section_key: ProposalTextSectionKey;
  body: string;
  included: boolean;
};

export function bodyFromOverrides(args: {
  formatKey: OpportunityFormatKey;
  archetype: ProposalPageArchetype;
  overrides: readonly SectionOverrideLike[];
}): ProposalPageBody {
  const byKey = new Map(args.overrides.map((o) => [o.section_key, o]));
  const take = (key: ProposalTextSectionKey): ProposalPageSectionPart => {
    const row = byKey.get(key);
    if (row) return part(row.body, row.included !== false);
    return part(getProposalTextDefault(args.formatKey, key), true);
  };
  switch (args.archetype) {
    case "cover_letter": {
      const body = take("cover_letter_body");
      const blocks = migrateCoverSandboxBlocks({
        bodyText: body.text,
        bodyIncluded: body.included,
      });
      return { body, blocks };
    }
    case "cost_breakdown": {
      const intro = take("cost_breakdown_intro");
      const footer = take("cost_breakdown_footer");
      const blocks = migrateCostSandboxBlocks({
        introText: intro.text,
        introIncluded: intro.included,
        footerText: footer.text,
        footerIncluded: footer.included,
      });
      return { intro, footer, blocks };
    }
    case "payment_schedule": {
      const intro = take("payment_schedule_intro");
      const footer = take("payment_schedule_footer");
      const blocks = migratePaymentSandboxBlocks({
        introText: intro.text,
        introIncluded: intro.included,
        footerText: footer.text,
        footerIncluded: footer.included,
      });
      return { intro, footer, blocks };
    }
    case "intent_to_purchase": {
      const preamble = take("intent_to_purchase_preamble");
      const signature_block = take("intent_to_purchase_signature_block");
      return {
        preamble,
        signature_block,
        blocks: defaultIntentSandboxBlocks({
          preambleText: preamble.text,
          signatureIntroText: signature_block.text,
        }),
      };
    }
  }
}

export function sectionsFromPageBody(
  archetype: ProposalPageArchetype,
  body: ProposalPageBody,
): Partial<Record<ProposalTextSectionKey, { body: string; included: boolean }>> {
  switch (archetype) {
    case "cover_letter": {
      const blocks = sandboxBlocksFromPageBody(archetype, body);
      const parts = coverPartsFromSandboxBlocks(blocks);
      return {
        cover_letter_body: { body: parts.body.text, included: parts.body.included },
      };
    }
    case "cost_breakdown": {
      const blocks = sandboxBlocksFromPageBody(archetype, body);
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        cost_breakdown_intro: { body: parts.intro.text, included: parts.intro.included },
        cost_breakdown_footer: {
          body: parts.footer.text,
          included: parts.footer.included,
        },
      };
    }
    case "payment_schedule": {
      const blocks = sandboxBlocksFromPageBody(archetype, body);
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        payment_schedule_intro: {
          body: parts.intro.text,
          included: parts.intro.included,
        },
        payment_schedule_footer: {
          body: parts.footer.text,
          included: parts.footer.included,
        },
      };
    }
    case "intent_to_purchase": {
      const blocks = intentBlocksFromBody(body as ProposalPageBodyIntent);
      const parts = textPartsFromSandboxBlocks(blocks);
      return {
        intent_to_purchase_preamble: {
          body: parts.preamble.text,
          included: parts.preamble.included,
        },
        intent_to_purchase_signature_block: {
          body: parts.signature_block.text,
          included: parts.signature_block.included,
        },
      };
    }
  }
}

export function archetypeToPageKind(archetype: ProposalPageArchetype): ProposalPageArchetype {
  return archetype;
}

export function pageKindToArchetype(
  pageKind: ProposalPageArchetype,
): ProposalPageArchetype {
  return pageKind;
}

export function normalizeProposalPageBody(
  formatKey: OpportunityFormatKey,
  archetype: ProposalPageArchetype,
  raw: unknown,
): ProposalPageBody {
  const fallback = defaultBodyForArchetype(formatKey, archetype);
  if (!raw || typeof raw !== "object") return fallback;
  const obj = raw as Record<string, unknown>;
  const readPart = (key: string, fb: ProposalPageSectionPart): ProposalPageSectionPart => {
    const v = obj[key];
    if (!v || typeof v !== "object") return fb;
    const p = v as Record<string, unknown>;
    const text = typeof p.text === "string" ? p.text : fb.text;
    return { text, included: p.included !== false };
  };
  switch (archetype) {
    case "cover_letter": {
      const fb = fallback as ProposalPageBodyCover;
      const body = readPart("body", fb.body);
      const blocks = migrateCoverSandboxBlocks({
        blocks: obj.blocks,
        bodyText: body.text,
        bodyIncluded: body.included,
      });
      const parts = coverPartsFromSandboxBlocks(blocks);
      return { body: part(parts.body.text, parts.body.included), blocks };
    }
    case "cost_breakdown": {
      const fb = fallback as ProposalPageBodyCost;
      const intro = readPart("intro", fb.intro);
      const footer = readPart("footer", fb.footer);
      const blocks = migrateCostSandboxBlocks({
        blocks: obj.blocks,
        introText: intro.text,
        introIncluded: intro.included,
        footerText: footer.text,
        footerIncluded: footer.included,
      });
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        intro: part(parts.intro.text, parts.intro.included),
        footer: part(parts.footer.text, parts.footer.included),
        blocks,
      };
    }
    case "payment_schedule": {
      const fb = fallback as ProposalPageBodyPayment;
      const intro = readPart("intro", fb.intro);
      const footer = readPart("footer", fb.footer);
      const blocks = migratePaymentSandboxBlocks({
        blocks: obj.blocks,
        introText: intro.text,
        introIncluded: intro.included,
        footerText: footer.text,
        footerIncluded: footer.included,
      });
      const parts = introFooterFromSandboxBlocks(blocks);
      return {
        intro: part(parts.intro.text, parts.intro.included),
        footer: part(parts.footer.text, parts.footer.included),
        blocks,
      };
    }
    case "intent_to_purchase": {
      const fb = fallback as ProposalPageBodyIntent;
      const preamble = readPart("preamble", fb.preamble);
      const signature_block = readPart("signature_block", fb.signature_block);
      const blocks = migrateIntentSandboxBlocks({
        blocks: obj.blocks,
        preambleText: preamble.text,
        preambleIncluded: preamble.included,
        signatureIntroText: signature_block.text,
        signatureIntroIncluded: signature_block.included,
        show_customer_sample: obj.show_customer_sample as boolean | undefined,
        signature_parties: obj.signature_parties,
      });
      const parts = textPartsFromSandboxBlocks(blocks);
      return {
        preamble: part(parts.preamble.text, parts.preamble.included),
        signature_block: part(parts.signature_block.text, parts.signature_block.included),
        blocks,
      };
    }
  }
}
